import { invariantResponse } from '@epic-web/invariant'
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline'
import { json, type LoaderFunctionArgs } from '@remix-run/node'
import { Link, useFetcher, useLoaderData, type MetaFunction } from '@remix-run/react'
import { brandName } from '#app/components/branding.js'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { RazorScriptDisplay } from '#app/components/razorScriptDisplay.js'
import Reactions from '#app/components/ui/reactions.js'
import ReportButton from '#app/components/ui/report-button.js'
import { getUserId } from '#app/utils/auth.server.js'
import { prisma } from '#app/utils/db.server.js'
import { getScriptReactions, getScriptReactionsByUser } from '#app/utils/reactions.server.js'
import { getScriptBySlug, getScriptById } from '#app/utils/scripts.server.js'

type FetcherData = {
	success?: boolean;
	error?: string;
  };
 

export async function loader({ request, params }: LoaderFunctionArgs) {

	var result = await getScriptBySlug(params.scriptSlug || "")
	if (!result && params.scriptSlug != null) {
		result = await getScriptById(params.scriptSlug || "")
	}
	invariantResponse(result, 'Script not found', { status: 404 })
	

	const serverDetails = await prisma.server.findFirst({
		where: { name: result.server }
	})

	const canEdit = result.creatorId === (await getUserId(request));

	// Fetch total counts for each reaction type
	const reactions = await getScriptReactions(result.id);
	const currentUser = await getUserId(request);
	const userReactions = await getScriptReactionsByUser(currentUser, result.id);

	const totalReactions: Record<string, number> = reactions.reduce(
		(acc, reaction) => {
		  acc[reaction.type] = reaction._count;
		  return acc;
		},
		{} as Record<string, number>
	  );

	return json({ script: result, totalReactions, canReact: !!currentUser, userReactions, canEdit, serverDetails })
}

export default function ViewScriptRoute() {
	const { script, totalReactions, canReact, userReactions, canEdit, serverDetails } = useLoaderData<typeof loader>()
	const fetcher = useFetcher<FetcherData>();

	const handleReact = (reaction: string, add: boolean) => {
		if (!canReact) {
			return;
		}
		fetcher.submit(
			{
			  contentType: "script",
			  type: reaction,
			  scriptId: script.id
			},
			{ method: add ? "POST" : "DELETE", action: "/reactions" }
		  );
	}

	return (
		<div className="container mb-48 mt-6 flex flex-col items-center justify-center">
			<div className="flex flex-wrap items-center justify-center">
				<h1 className="text-center text-h2">
					<Link to={`/scripts/${serverDetails?.slug}`} className="underline hover:no-underline">{script.server}</Link>: {script.title}
				</h1>
			</div>
			<div className='p-4'>
				<Reactions totalReactions={totalReactions} userReactions={userReactions} onReact={handleReact} />
				{fetcher.state === "submitting" && <p>Submitting your reaction...</p>}
				{fetcher.state === "idle" && fetcher.data?.success && <p>Reaction submitted!</p>}
				{fetcher.state === "idle" && fetcher.data?.error && <p className="text-red-500">Failed to submit reaction.</p>}
			</div>

			<div className="mt-2 text-center text-muted-foreground">
				<span className='mr-8'>
					Creator: {' '}
					{script.creator ? <Link to={`/users/${script.creator?.username}/scripts`} className="underline hover:no-underline"><span>{script.creator?.username}</span></Link> : "Anonymous Upload" } 
				</span>
				
				Link: {' '}
				<a href={`/script/${script.slug}`} className="underline hover:no-underline"><span>{script.slug}</span>
					<ArrowTopRightOnSquareIcon aria-hidden="true" className="h-6 w-6 inline p-1" />
				</a>
				<ReportButton className="ml-8 text-red-500 underline hover:text-red-700 hover:no-underline" contentId={script.id} contentType="script" />
			</div>

			<div className="container flex flex-col rounded-3xl bg-muted mt-4 pt-4 p-12">
				<RazorScriptDisplay 
					razorScript={script} 
					razorScriptOwner={script.creatorId}
					isOwner={canEdit}
					fileName="" 
				/>
			</div>


			<Link to="/scripts" className="mt-8 underline hover:no-underline">View list of scripts</Link>
		</div>
	)
}

export const meta: MetaFunction<typeof loader> = ({ data, params }) => {
	const displayName = data?.script.title ?? params.scriptSlug
	return [
		{ title: `${displayName} | ${brandName}` },
		{
			name: 'description',
			content: `Script ${displayName} on ${brandName}`,
		},
		{
			tagName: "link",
			rel: "canonical",
			href: `https://uoaddicts.com/script/${data?.script.slug ?? params.scriptSlug}`
		},
	]
}

export function ErrorBoundary() {
	return (
		<GeneralErrorBoundary
			statusHandlers={{
				404: ({ params }) => (
					<div className="text-center">
						<p className="m-4">Cannot find a script related to: <br />"{params.scriptSlug}"<br /> reach out if you think it should be added.</p>
						<Link to="/scripts" className="text-xl underline hover:no-underline">&lt; Back to all scripts</Link>
						<Link to="/script/new" className="ml-8 text-xl underline hover:no-underline">Add it myself</Link>
					</div>					
				),
			}}
		/>
	)
}
